import React from "react";
import { Button, Icon, StatusBadge } from "../../components/tailwind";
import type { StatusBadgeColor } from "../../components/tailwind";
import { Spinner } from "../../utils/Spinner";
import { EnrichFieldRenderer } from "./EnrichFieldRenderer";
import { EnrichedField, EnrichStatus } from "../../api/enrichApi";
import { ValidatedFieldsetField } from "../../api/fieldsetApi";

const getStatusBadgeColor = (status: EnrichStatus): StatusBadgeColor => {
  switch (status) {
    case EnrichStatus.NOT_ENRICHED:
      return "yellow";
    case EnrichStatus.ENRICHED:
      return "green";
    case EnrichStatus.ENRICHED_AND_SENT_TO_PIM:
      return "blue";
    case EnrichStatus.FAILED_TO_SEND_TO_PIM:
      return "red";
    default:
      return "gray";
  }
};

type Props = {
  field: EnrichedField;
  validationField?: ValidatedFieldsetField;
  isEnrichingField: boolean;
  disabled: boolean;
  isSelectable: boolean;
  enrichFieldIds: Set<number>;
  onEnrichFieldIdToggle: (customerFieldId: number) => void;
  onSave: (id: number, value: any) => Promise<void>;
  onRevert: (id: number) => void;
  onEnrichField: () => void;
  onEditField: (customerFieldId: number) => void;
  onCreateField?: (fieldKey: string) => void;
  isCreatingField?: boolean;
};

export const EnrichFieldRow: React.FC<Props> = ({
  field,
  validationField,
  isEnrichingField,
  disabled,
  isSelectable,
  enrichFieldIds,
  onEnrichFieldIdToggle,
  onSave,
  onRevert,
  onEnrichField,
  onEditField,
  onCreateField,
  isCreatingField = false,
}) => {
  const isDisabled = disabled || isEnrichingField;
  const isValidationPreview = !!validationField;
  const isRowExcludedFromEnrich =
    field.customer_field_id != null &&
    !enrichFieldIds.has(field.customer_field_id);
  const valueInputDisabled =
    isDisabled || isValidationPreview || isRowExcludedFromEnrich;
  const buttonsDisabled = isDisabled || isRowExcludedFromEnrich;

  let statusLabel: string;
  let statusColor: StatusBadgeColor;
  let statusTooltip: string | undefined;

  if (isValidationPreview && validationField) {
    if (!validationField.is_valid_field) {
      statusLabel = "Invalid";
      statusColor = "red";
      statusTooltip = validationField.error ?? undefined;
    } else if (validationField.value != null && validationField.value !== "") {
      statusLabel = "Has existing PIM value";
      statusColor = "amber";
    } else {
      statusLabel = EnrichStatus.NOT_ENRICHED;
      statusColor = getStatusBadgeColor(EnrichStatus.NOT_ENRICHED);
    }
  } else if (field.send_error) {
    statusLabel = "Send to PIM Failed";
    statusColor = getStatusBadgeColor(EnrichStatus.FAILED_TO_SEND_TO_PIM);
    statusTooltip = field.send_error;
  } else if (field.published) {
    statusLabel = "Sent to PIM";
    statusColor = getStatusBadgeColor(EnrichStatus.ENRICHED_AND_SENT_TO_PIM);
    statusTooltip = new Date(field.published).toLocaleString();
  } else {
    statusLabel = "Enriched";
    statusColor = getStatusBadgeColor(EnrichStatus.ENRICHED);
  }

  const includeCheckboxHelp = ((): string => {
    if (field.customer_field_id == null) {
      return "";
    }
    if (
      isValidationPreview &&
      validationField != null &&
      !validationField.is_valid_field
    ) {
      return "This field cannot be included until its configuration is fixed.";
    }
    if (!isSelectable) {
      return "This field is not part of the selected Input Schema.";
    }
    if (isEnrichingField) {
      return "This field is currently being enriched.";
    }
    if (disabled) {
      return "Selection cannot be changed while an enrich job is in progress.";
    }
    return "Toggle to include";
  })();

  const includeCheckboxBalloonProps =
    includeCheckboxHelp !== ""
      ? {
          "data-balloon": includeCheckboxHelp,
          "data-balloon-pos": "right" as const,
        }
      : {};

  const dimExcludedRow = isRowExcludedFromEnrich && !isEnrichingField;

  const includeCheckboxDisabled =
    !isSelectable ||
    isDisabled ||
    (isValidationPreview &&
      validationField != null &&
      !validationField.is_valid_field);
  const canToggleIncludeByRowClick =
    field.customer_field_id != null && !includeCheckboxDisabled;

  const handleRowBackgroundClick = (): void => {
    if (!canToggleIncludeByRowClick || field.customer_field_id == null) {
      return;
    }
    onEnrichFieldIdToggle(field.customer_field_id);
  };

  const stopRowTogglePropagation = (
    e: React.MouseEvent<HTMLTableCellElement>
  ): void => {
    e.stopPropagation();
  };

  return (
    <tr
      className={`${
        isEnrichingField
          ? "tw-opacity-60 hover:tw-bg-gray-50"
          : dimExcludedRow
          ? "tw-bg-gray-50 tw-opacity-70 hover:tw-bg-gray-100/90"
          : "hover:tw-bg-gray-50"
      }${canToggleIncludeByRowClick ? " tw-cursor-pointer" : ""}`}
      onClick={handleRowBackgroundClick}
      title={
        canToggleIncludeByRowClick
          ? "Click to toggle include in enrich"
          : undefined
      }
    >
      <td className="tw-px-4 tw-py-4 tw-text-sm">
        {field.customer_field_id != null ? (
          <span
            className="tw-inline-flex tw-items-center"
            {...includeCheckboxBalloonProps}
          >
            <input
              type="checkbox"
              checked={enrichFieldIds.has(field.customer_field_id)}
              disabled={includeCheckboxDisabled}
              onChange={() => onEnrichFieldIdToggle(field.customer_field_id)}
              onClick={(e) => e.stopPropagation()}
              aria-label="Include this field in enrich"
              data-testid="enrich-field-row-include"
              className="tw-rounded tw-border-gray-300"
            />
          </span>
        ) : null}
      </td>
      <td
        className={`tw-px-6 tw-py-4 tw-text-sm tw-font-medium ${
          dimExcludedRow ? "tw-text-gray-500" : "tw-text-gray-900"
        }`}
      >
        <span className="tw-truncate">{field.display_key}</span>
      </td>
      <td
        className={`tw-px-6 tw-py-4 tw-text-sm ${
          dimExcludedRow ? "tw-text-gray-500" : "tw-text-gray-900"
        }`}
        onClick={stopRowTogglePropagation}
      >
        <EnrichFieldRenderer
          field={field}
          onSave={onSave}
          disabled={valueInputDisabled}
        />
      </td>
      <td
        className={`tw-px-6 tw-py-4 tw-text-sm ${
          dimExcludedRow ? "tw-text-gray-400" : "tw-text-gray-600"
        }`}
      >
        {field.reasoning || "-"}
      </td>
      <td
        className={`tw-px-6 tw-py-4 tw-text-sm ${
          dimExcludedRow ? "tw-opacity-80" : ""
        }`}
      >
        <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start">
          <StatusBadge
            label={statusLabel}
            color={statusColor}
            tooltip={statusTooltip}
            tooltipPos="left"
          />
          {!isValidationPreview && field.is_edited && (
            <StatusBadge label="Edited" color="violet" />
          )}
          {!isValidationPreview &&
            field.existing_pim_value &&
            !field.published && (
              <StatusBadge
                label="Has existing PIM Value"
                color="amber"
                tooltip={`Current PIM value: ${field.existing_pim_value}`}
                tooltipPos="left"
              />
            )}
        </div>
      </td>
      <td
        className={`tw-px-6 tw-py-4 tw-text-sm ${
          dimExcludedRow ? "tw-opacity-80" : ""
        }`}
        onClick={stopRowTogglePropagation}
      >
        {isEnrichingField || (isValidationPreview && isCreatingField) ? (
          <Spinner />
        ) : (
          <div className="tw-flex tw-items-center tw-gap-1">
            {isValidationPreview ? (
              field.customer_field_id ? (
                <Button
                  onClick={() => onEditField(field.customer_field_id)}
                  disabled={isDisabled}
                  className="tw-p-1 tw-text-gray-500 hover:!tw-brightness-100 hover:!tw-text-white hover:!tw-shadow-sm !tw-border-gray-500 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                  title="Edit field configuration"
                  size="sm"
                >
                  <Icon name="settings" className="tw-text-base" />
                </Button>
              ) : (
                onCreateField && (
                  <Button
                    onClick={() => onCreateField(field.key)}
                    disabled={isDisabled}
                    className="tw-p-1 tw-text-gray-500 hover:!tw-brightness-100 hover:!tw-text-white hover:!tw-shadow-sm !tw-border-gray-500 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    title="Create PIM Field"
                    size="sm"
                  >
                    <Icon name="add" className="tw-text-base" />
                  </Button>
                )
              )
            ) : (
              <>
                {field.is_edited && (
                  <Button
                    onClick={() => onRevert(field.id)}
                    disabled={buttonsDisabled}
                    className="tw-p-1 tw-text-gray-500 hover:!tw-brightness-100 hover:!tw-text-white hover:!tw-shadow-sm !tw-border-gray-500 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    title="Revert to previous value"
                    size="sm"
                  >
                    <Icon name="undo" />
                  </Button>
                )}
                {field.customer_field_id && (
                  <Button
                    onClick={onEnrichField}
                    disabled={buttonsDisabled}
                    className="tw-p-1 !tw-border-textual-blue !tw-text-textual-blue hover:!tw-bg-textual-blue hover:!tw-brightness-100 hover:!tw-text-white hover:!tw-shadow-sm disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    title="Enrich only this field"
                    size="sm"
                  >
                    <Icon name="auto_fix_high" className="tw-text-base" />
                  </Button>
                )}
                {field.customer_field_id && (
                  <Button
                    onClick={() => onEditField(field.customer_field_id)}
                    disabled={buttonsDisabled}
                    className="tw-p-1 tw-text-gray-500 hover:!tw-brightness-100 hover:!tw-text-white hover:!tw-shadow-sm !tw-border-gray-500 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    title="Edit field instructions"
                    size="sm"
                  >
                    <Icon name="settings" className="tw-text-base" />
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};
